/**
 * Pure keyboard model of the Azure picker tree (WAI-ARIA tree pattern, roving
 * tabindex). The tree is flattened to its VISIBLE rows in DOM order; a key
 * press maps `(rows, activeKey, key)` to the next active row and the action the
 * component performs:
 *
 *  - Up / Down: previous / next visible row (no wrap); Home / End: first / last.
 *  - Right: expand a closed group, else move to its first child.
 *  - Left: collapse an open group, else move to the parent.
 *  - Space: `toggle` (a resource's selection; a group's checkbox in multi mode,
 *    else its expansion).
 *  - Enter: `confirm` a resource (single mode; `toggle` in multi mode), or
 *    toggle a group's expansion.
 */
import type { AzureDiscoveredResource } from '../../../types/azureDiscovery';
import type { AzureTreeGroup } from './azurePickerFilter';

export type TreeRowKind = 'subscription' | 'kind' | 'resource';

export interface TreeRow {
  key: string;
  kind: TreeRowKind;
  /** 1 subscription, 2 kind, 3 resource (`aria-level`). */
  level: 1 | 2 | 3;
  parentKey?: string;
  /** Groups only. */
  open?: boolean;
  resource?: AzureDiscoveredResource;
  disabled?: boolean;
}

export const subscriptionKey = (subscriptionId: string): string => subscriptionId;
export const kindKey = (subscriptionId: string, kind: string): string => `${subscriptionId}::${kind}`;

/** The visible rows in DOM order for the given open state. */
export function flattenVisibleRows(
  groups: AzureTreeGroup[],
  isOpen: (key: string) => boolean,
  disabledIds: Set<string>
): TreeRow[] {
  const rows: TreeRow[] = [];
  for (const group of groups) {
    const subKey = subscriptionKey(group.subscriptionId);
    const subOpen = isOpen(subKey);
    rows.push({ key: subKey, kind: 'subscription', level: 1, open: subOpen });
    if (!subOpen) continue;
    for (const kind of group.kinds) {
      const kKey = kindKey(group.subscriptionId, kind.kind);
      const kindOpen = isOpen(kKey);
      rows.push({ key: kKey, kind: 'kind', level: 2, parentKey: subKey, open: kindOpen });
      if (!kindOpen) continue;
      for (const resource of kind.resources) {
        rows.push({ key: resource.id, kind: 'resource', level: 3, parentKey: kKey, resource, disabled: disabledIds.has(resource.id) });
      }
    }
  }
  return rows;
}

export type TreeKeyAction =
  | { type: 'move'; key: string }
  | { type: 'expand'; key: string }
  | { type: 'collapse'; key: string }
  | { type: 'toggle'; key: string }
  | { type: 'confirm'; key: string }
  | { type: 'none' };

export function treeKeyAction(rows: TreeRow[], activeKey: string | undefined, key: string): TreeKeyAction {
  if (rows.length === 0) return { type: 'none' };
  const index = activeKey === undefined ? -1 : rows.findIndex((row) => row.key === activeKey);
  const active = index >= 0 ? rows[index] : undefined;

  switch (key) {
    case 'ArrowDown':
      return index < 0 ? { type: 'move', key: rows[0].key } : index + 1 < rows.length ? { type: 'move', key: rows[index + 1].key } : { type: 'none' };
    case 'ArrowUp':
      return index < 0 ? { type: 'move', key: rows[rows.length - 1].key } : index > 0 ? { type: 'move', key: rows[index - 1].key } : { type: 'none' };
    case 'Home':
      return { type: 'move', key: rows[0].key };
    case 'End':
      return { type: 'move', key: rows[rows.length - 1].key };
    case 'ArrowRight': {
      if (!active) return { type: 'move', key: rows[0].key };
      if (active.kind === 'resource') return { type: 'none' };
      if (!active.open) return { type: 'expand', key: active.key };
      const child = rows[index + 1];
      return child && child.parentKey === active.key ? { type: 'move', key: child.key } : { type: 'none' };
    }
    case 'ArrowLeft': {
      if (!active) return { type: 'none' };
      if (active.kind !== 'resource' && active.open) return { type: 'collapse', key: active.key };
      return active.parentKey ? { type: 'move', key: active.parentKey } : { type: 'none' };
    }
    case ' ':
    case 'Spacebar':
      return active ? { type: 'toggle', key: active.key } : { type: 'none' };
    case 'Enter':
      return active ? { type: 'confirm', key: active.key } : { type: 'none' };
    default:
      return { type: 'none' };
  }
}
